import { ReactNode } from "react";

import {
  DefaultChargeTable,
  NoDiscountBreakdownTable,
  V3ChargeTable,
} from "./components/ChargeTables";
import {
  StyledChangeTag,
  StyledDialogField,
  StyledDialogLabel,
  StyledDialogMock,
  StyledDisclaimerList,
  StyledDisclaimerTitle,
  StyledDocTitle,
  StyledFacilityMeta,
  StyledFlowNote,
  StyledLogoBox,
  StyledMarkedBlock,
  StyledNoteText,
  StyledPage,
  StyledPageBody,
  StyledPageFooter,
  StyledPageHeader,
  StyledPanelHeader,
  StyledPanelTitle,
  StyledParagraph,
  StyledPatientGrid,
  StyledPatientItem,
  StyledPhoneMock,
  StyledPreviewPanel,
  StyledPromptPayBox,
  StyledSignLine,
  StyledVariantPill,
} from "./styled";

/** Stand-in order so the mock reads like a real estimate. */
const previewOrder = {
  facilityName: "General Hospital",
  address: "123 Main Street, Springfield, IL",
  phone: "555-010-1234",
  patientName: "Jane Q. Patient",
  dateOfBirth: "01/15/1980",
  payerName: "Blue Cross PPO",
  serviceDate: "03/14/2026",
  accountNumber: "100234",
  procedures: "45380 Colonoscopy with biopsy · 00810 Anesthesia · Facility fee",
  note: "Please arrive 45 minutes early and bring a photo ID.",
  hoursEstimated: "2.5",
};

export interface IPdfPreviewProps {
  noDiscountPdf?: boolean;
  addNoteToPdf?: boolean;
  version3Pdf?: boolean;
}

/** Names the toggle a block owes its presence to. */
function PreviewBlock({
  tag,
  icon = "pi pi-bolt",
  children,
}: {
  tag: string;
  icon?: string;
  children: ReactNode;
}) {
  return (
    <StyledMarkedBlock>
      <StyledChangeTag>
        <i className={icon} />
        {tag}
      </StyledChangeTag>
      {children}
    </StyledMarkedBlock>
  );
}

/**
 * Compact mock of the PDF an internal user downloads from the order page,
 * mirroring the branches in ClearOrderDocument so each toggle shows its effect
 * before it is saved. Values are placeholders — only the layout is real.
 *
 * Not covered here: providers with their own custom PDF content, which replaces
 * this document wholesale and is configured on the provider, not on Customize.
 */
export function PdfPreview({
  noDiscountPdf,
  addNoteToPdf,
  version3Pdf,
}: IPdfPreviewProps) {
  // Version 3 owns the layout wherever the two overlap, matching the document.
  const showPromptPay = !noDiscountPdf && !version3Pdf;
  const showSpanish = !version3Pdf;
  // Version 3 routes through the note dialog whether or not the toggle is on.
  const opensNoteDialog = !!addNoteToPdf || !!version3Pdf;
  // The note is only printed by the two layouts that make room for it.
  const printsNote = !!noDiscountPdf || !!version3Pdf;
  const showSignLine = !!noDiscountPdf || !!version3Pdf;

  let variantLabel = "Standard PDF";
  if (version3Pdf) {
    variantLabel = "PDF version 3";
  } else if (noDiscountPdf) {
    variantLabel = "No-discount PDF";
  }

  return (
    <StyledPreviewPanel data-test-id="customize-pdf-preview">
      <StyledPanelHeader>
        <StyledPanelTitle>
          <i className="pi pi-file-pdf" />
          DOWNLOADED PDF
        </StyledPanelTitle>
        <StyledVariantPill>{variantLabel}</StyledVariantPill>
      </StyledPanelHeader>

      <StyledFlowNote>
        <i className="pi pi-info-circle" />
        <span>
          {opensNoteDialog
            ? "Download PDF opens a dialog first, then downloads the file."
            : "Download PDF downloads the file straight away."}
        </span>
      </StyledFlowNote>

      {addNoteToPdf && !printsNote && (
        <StyledFlowNote $tone="warn">
          <i className="pi pi-exclamation-triangle" />
          <span>
            The note is saved to order history but is not printed on this
            layout. Turn on No-Discount PDF or PDF version 3 to show it.
          </span>
        </StyledFlowNote>
      )}

      {opensNoteDialog && (
        <StyledDialogMock>
          <strong>Add Note for PDF</strong>
          Please, add your note here!
          <StyledDialogField>{previewOrder.note}</StyledDialogField>
          {version3Pdf && (
            <>
              <StyledDialogLabel>Estimated Hours (required)</StyledDialogLabel>
              <StyledDialogField $short>
                {previewOrder.hoursEstimated}
              </StyledDialogField>
            </>
          )}
        </StyledDialogMock>
      )}

      <StyledPage>
        <StyledPageBody>
          <StyledPageHeader>
            <StyledLogoBox>LOGO</StyledLogoBox>
            <StyledFacilityMeta>
              {previewOrder.address}
              <br />
              {previewOrder.phone}
            </StyledFacilityMeta>
          </StyledPageHeader>

          <StyledDocTitle>Good Faith Estimate</StyledDocTitle>

          <StyledParagraph>
            This estimate uses procedure information provided by your physician
            as well as the benefits available from your insurance company (if
            applicable). This is not a guarantee of the final amount due.
          </StyledParagraph>

          {showSpanish && (
            <StyledParagraph>
              Esta estimación utiliza la información del procedimiento
              proporcionada por su médico, así como los beneficios disponibles
              de su compañía de seguros (si corresponde). Esto no es una
              garantía del monto final adeudado.
            </StyledParagraph>
          )}

          <StyledPatientGrid>
            <StyledPatientItem>
              <span>Patient</span>
              <strong>{previewOrder.patientName}</strong>
            </StyledPatientItem>
            <StyledPatientItem>
              <span>Date of birth</span>
              <strong>{previewOrder.dateOfBirth}</strong>
            </StyledPatientItem>
            <StyledPatientItem>
              <span>Account #</span>
              <strong>{previewOrder.accountNumber}</strong>
            </StyledPatientItem>
            <StyledPatientItem>
              <span>Insurance</span>
              <strong>{previewOrder.payerName}</strong>
            </StyledPatientItem>
            <StyledPatientItem>
              <span>Date of service</span>
              <strong>{previewOrder.serviceDate}</strong>
            </StyledPatientItem>
            <StyledPatientItem>
              <span>Services</span>
              <strong>{previewOrder.procedures}</strong>
            </StyledPatientItem>
          </StyledPatientGrid>

          {version3Pdf && (
            <PreviewBlock tag="PDF version 3">
              <V3ChargeTable />
            </PreviewBlock>
          )}

          {!version3Pdf && noDiscountPdf && (
            <PreviewBlock tag="No-Discount PDF">
              <NoDiscountBreakdownTable />
            </PreviewBlock>
          )}

          {!version3Pdf && !noDiscountPdf && <DefaultChargeTable />}

          {showPromptPay && (
            <StyledPromptPayBox>
              <span>
                To qualify for the <strong>Prompt Pay Discount</strong> you need
                to pay before April 25, 2026 using the link provided to you via
                text through our Clear Health Partner. For more information,
                please contact Clear Health at 1-888-918-2522.
              </span>
              <StyledPhoneMock>
                <i className="pi pi-mobile" />
              </StyledPhoneMock>
            </StyledPromptPayBox>
          )}

          {printsNote && (
            // Tagged as the note because version 3 is the only layout that
            // labels it on the page itself — no-discount prints it bare, so
            // without the tag it reads as body copy.
            <PreviewBlock
              icon="pi pi-pencil"
              tag={`Note · ${
                version3Pdf ? "PDF version 3" : "No-Discount PDF"
              }`}
            >
              <StyledNoteText>
                {version3Pdf && <strong>Note added from office: </strong>}
                {previewOrder.note}
              </StyledNoteText>
            </PreviewBlock>
          )}

          {version3Pdf && (
            <PreviewBlock tag="PDF version 3">
              <StyledDisclaimerTitle>
                Important disclaimer
              </StyledDisclaimerTitle>
              <StyledNoteText>
                <strong>
                  We have estimated your surgery to be{" "}
                  {previewOrder.hoursEstimated} hours, however, the actual time
                  of surgery may vary.
                </strong>
              </StyledNoteText>
              <StyledNoteText>
                The total out-of-pocket cosmetic costs for the following fees
                are due as follows:
              </StyledNoteText>
              <StyledDisclaimerList>
                <li>
                  <strong>Surgeon fees</strong> – due two weeks before your
                  surgery date.
                </li>
                <li>
                  <strong>Anesthesia fees</strong> – due two weeks before your
                  surgery date.
                </li>
                <li>
                  <strong>Facility fees</strong> – due two weeks before your
                  surgery.
                </li>
              </StyledDisclaimerList>
            </PreviewBlock>
          )}

          {showSignLine && <StyledSignLine>Sign here</StyledSignLine>}
        </StyledPageBody>

        <StyledPageFooter>{previewOrder.facilityName}</StyledPageFooter>
      </StyledPage>
    </StyledPreviewPanel>
  );
}
